import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import './AdminFiles.css';
import LoadingCircle from '../../components/Loading/LoadingCircle'; // Import LoadingCircle
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { getStoredData } from "../../JsonFiles/fetchData";

const AdminFiles = () => {
  const [files, setFiles] = useState([]); // State to store all files
  const [displayFiles, setDisplayFiles] = useState([]); // State for the files to be displayed
  const [uploading, setUploading] = useState(false); // State for upload status
  const [selectedFiles, setSelectedFiles] = useState([]); // State for selected files
  const [page, setPage] = useState(1); // Current page for pagination
  const [itemsPerPage] = useState(20); // Number of files to show per page
  const [searchTerm, setSearchTerm] = useState(""); // State for search term
  const [isModalOpen, setIsModalOpen] = useState(false); // State to control modal visibility
  const [modalImage, setModalImage] = useState(""); // State to store the image to be displayed in the modal
  const [isLoading, setIsLoading] = useState(true); // State to control the loading state

  const totalData = getStoredData();
  const data = totalData ? totalData["11"].AdminFileManagement : {};

  const fetchFiles = async () => {
    try {
      const response = await axios.get(data.ApiView); // Use dynamic API URL
      setFiles(response.data.files); // Set the files in state
      setIsLoading(false); // Set loading state to false after files are fetched
    } catch (error) {
      console.error('Error fetching files:', error);
      setIsLoading(false); // In case of error, stop loading
    }
  };

  useEffect(() => {
    fetchFiles(); // Fetch files when the component mounts
  }, []);

  useEffect(() => {
    const filteredFiles = files.filter((file) =>
      file.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setDisplayFiles(filteredFiles); // Update display files with search results
    setPage(1); // Reset pagination to the first page after search
  }, [searchTerm, files]);

  const handleFileUpload = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select at least one file to upload');
      return;
    }
  
    setUploading(true);
    setIsLoading(true); // Show loading spinner when uploading files
  
    const formData = new FormData();
    selectedFiles.forEach((file) => {
      formData.append('files', file); // Append each selected file to formData
    });
  
    try {
      const response = await axios.post(data.ApiUpload, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
  
      if (response.data.success) {
        alert('Files uploaded successfully!');
        fetchFiles(); // Re-fetch the files after successful upload
        setSelectedFiles([]); // Clear the selected files after upload
      } else {
        alert('Error uploading files.');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Error uploading files.');
    } finally {
      setUploading(false);
      setIsLoading(false); // Stop loading after the upload completes
    }
  };
  
  const handleDeleteSelected = async () => {
    if (selectedFiles.length === 0) {
      alert('No files selected for deletion');
      return;
    }
  
    const fileNames = selectedFiles.map((file) => file.name); // Get file names from selected files
    setIsLoading(true); // Show loading while deleting files
  
    try {
      const response = await axios.delete(data.ApiDelete, {
        data: { fileNames }, // Send the list of selected file names
      });
  
      if (response.data.success) {
        alert('Selected files deleted successfully!');
        fetchFiles(); // Re-fetch the files after successful deletion
        setSelectedFiles([]); // Clear selected files after deletion
      } else {
        alert('Error deleting files.');
      }
    } catch (error) {
      console.error('Error deleting files:', error);
      alert('Error deleting files.');
    } finally {
      setIsLoading(false); // Stop loading after the delete completes
    }
  };
  

  const isImage = (file) => {
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'];
    const fileName = file.name || ''; // Ensure it's a string
    return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext)); // Safe to call toLowerCase now
  };

  const indexOfLastFile = page * itemsPerPage;
  const indexOfFirstFile = indexOfLastFile - itemsPerPage;
  const currentFiles = displayFiles.slice(indexOfFirstFile, indexOfLastFile);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const openModal = (imageSrc) => {
    setModalImage(imageSrc);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImage(""); // Reset modal image
  };

  const onDrop = (acceptedFiles) => {
    setSelectedFiles(acceptedFiles); // Set the dropped files to selectedFiles state
  };

  const toggleFileSelection = (file) => {
    setSelectedFiles((prevSelectedFiles) => {
      if (prevSelectedFiles.includes(file)) {
        return prevSelectedFiles.filter((f) => f !== file); // Remove file if already selected
      } else {
        return [...prevSelectedFiles, file]; // Add file if not selected
      }
    });
  };

  // Define the handleRemoveFile function to remove a file from the selected files list
  const handleRemoveFile = (fileToRemove) => {
    setSelectedFiles((prevSelectedFiles) =>
      prevSelectedFiles.filter((file) => file !== fileToRemove)
    );
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: true, // Allow multiple file uploads
    accept: 'image/*', // Only accept image files (you can adjust this as needed)
  });

  return (
    <div className="admin-files">
      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          placeholder="Search files..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* File upload form with drag-and-drop */}
      <div {...getRootProps({ className: 'dropzone' })}>
        <input {...getInputProps()} />
        <p>{data.DropZoneText}</p> {/* Using dynamic text from JSON */}
      </div>

      {/* Selected Files Preview */}
      {selectedFiles.length > 0 && (
        <div className="selected-files-preview">
          <h3>{data.SelectedFilePreviewText}</h3> {/* Dynamic text */}
          <ul>
            {selectedFiles.map((file, index) => (
              <li key={index} className="selected-file-item">
                <span>{file.name}</span>
                <button
                  className="delete-file-button"
                  onClick={() => handleRemoveFile(file)}
                  title="Remove file"
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Buttons Container */}
      <div className="button-container">
        {/* Upload Button */}
        <button onClick={handleFileUpload} disabled={uploading}>
          {uploading ? 'Uploading...' : 'Upload Files'}
        </button>

        {/* Delete Selected Files Button */}
        <button className="delete" onClick={handleDeleteSelected} disabled={selectedFiles.length === 0}>
          {isLoading ? 'Deleting...' : 'Delete Selected Files'}
        </button>
      </div>

      {isLoading && <LoadingCircle />}

      {/* File List */}
      <div className="file-list">
        {!isLoading && currentFiles.length ? (
          currentFiles.map((file, index) => {
            const cleanFileName = file.name.replace(/^\/+/g, '');
            const extension = cleanFileName.split('.').pop();
            const fileNameWithoutExtension = cleanFileName.replace(`.${extension}`, '');

            return (
              <div className="file-item" key={index}>
                <input
                  type="checkbox"
                  checked={selectedFiles.includes(file)}
                  onChange={() => toggleFileSelection(file)}
                />
                {isImage(file) ? (
                  <div className="image-container" onClick={() => openModal(`/images/${cleanFileName}`)}>
                    <h3 className="file-name">{fileNameWithoutExtension}</h3>
                    <img
                      src={`/images/${cleanFileName}`}
                      alt={fileNameWithoutExtension}
                      className="file-image"
                      onError={(e) => e.target.src = data.NoPhotoImg} // Fallback if image not found
                    />
                    <div className="file-extension-bubble">{extension}</div>
                  </div>
                ) : (
                  <a href={`/files/${cleanFileName}`} target="_blank" rel="noopener noreferrer">
                    {cleanFileName}
                  </a>
                )}
              </div>
            );
          })
        ) : (
          !isLoading && <p>No files found.</p> // Show message when no files are found
        )}
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button
          className={`pagination-button ${page === 1 ? 'disabled' : ''}`}
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
        >
          &larr; Previous
        </button>

        <span className="pagination-info">{page}</span>

        <button
          className={`pagination-button ${
            indexOfLastFile >= displayFiles.length ? 'disabled' : ''
          }`}
          onClick={() => handlePageChange(page + 1)}
          disabled={indexOfLastFile >= displayFiles.length}
        >
          Next &rarr;
        </button>
      </div>

      {/* Modal for Image Zoom */}
      {isModalOpen && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <img src={modalImage} alt="Zoomed Image" className="modal-image" />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFiles;
